import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PanelLayout from '../components/PanelLayout';
import { API } from '../utils/api';
import { Plus, Trash2, MapPin, Phone, Mail, Globe, ExternalLink, Users, Check, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BUSINESS_TYPES = {
  vet: "Weterynarz",
  trainer: "Trener/Szkoleniowiec",
  groomer: "Groomer",
  pet_shop: "Sklep zoologiczny",
  photographer: "Fotograf zwierząt",
  nutritionist: "Dietetyk/Żywieniowiec",
  breeder: "Inna hodowla",
  other: "Inny"
};

const STATUS_BADGES = {
  pending: { label: "Oczekuje", icon: Clock, className: "bg-yellow-500/20 text-yellow-400" },
  published: { label: "Opublikowana", icon: Check, className: "bg-green-500/20 text-green-400" },
  rejected: { label: "Odrzucona", icon: X, className: "bg-red-500/20 text-red-400" }
};

export default function Recommendations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    business_type: 'vet',
    business_description: '',
    business_city: '',
    business_voivodeship: '',
    business_phone: '',
    business_email: '',
    business_website: '',
    business_facebook: '',
    business_instagram: '',
    recommendation_text: ''
  });

  const VOIVODESHIPS = [
    "dolnośląskie", "kujawsko-pomorskie", "lubelskie", "lubuskie",
    "łódzkie", "małopolskie", "mazowieckie", "opolskie",
    "podkarpackie", "podlaskie", "pomorskie", "śląskie",
    "świętokrzyskie", "warmińsko-mazurskie", "wielkopolskie", "zachodniopomorskie"
  ];

  useEffect(() => {
    fetchRecommendations();
  }, [token]);

  const fetchRecommendations = async () => {
    try {
      const res = await fetch(`${API}/api/recommendations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.business_city || !form.recommendation_text) {
      alert('Wypełnij wymagane pola: nazwa, miasto i treść rekomendacji');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        const newRec = await res.json();
        setRecommendations([newRec, ...recommendations]);
        setShowForm(false);
        setForm({
          business_name: '',
          business_type: 'vet',
          business_description: '',
          business_city: '',
          business_voivodeship: '',
          business_phone: '',
          business_email: '',
          business_website: '',
          business_facebook: '',
          business_instagram: '',
          recommendation_text: ''
        });
      } else {
        const err = await res.json();
        alert(err.detail || 'Błąd tworzenia rekomendacji');
      }
    } catch (err) {
      console.error('Error creating recommendation:', err);
      alert('Błąd sieci');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę rekomendację?')) return;

    try {
      const res = await fetch(`${API}/api/recommendations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setRecommendations(recommendations.filter(r => r.id !== id));
      }
    } catch (err) {
      console.error('Error deleting recommendation:', err);
    }
  };

  return (
    <PanelLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-7 h-7 text-orange-400" />
              Sieć Rekomendacji AI
            </h1>
            <p className="text-zinc-400 mt-1">
              Polecaj lokalne firmy i buduj swoją sieć partnerów. Rekomendacje zwiększają Twoją widoczność w AI.
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition"
            data-testid="add-recommendation-btn"
          >
            <Plus className="w-5 h-5" />
            Dodaj rekomendację
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-gradient-to-r from-orange-500/10 to-orange-500/10 border border-orange-500/20 rounded-xl p-4">
          <h3 className="text-white font-medium mb-2">💡 Jak działa Sieć Rekomendacji?</h3>
          <ul className="text-gray-300 text-sm space-y-1">
            <li>• Polecaj zaufanych weterynarzy, trenerów, groomerów i innych specjalistów</li>
            <li>• Twoje rekomendacje pojawią się na Twoim profilu publicznym</li>
            <li>• Inne firmy mogą polecać Twoją hodowlę - budując wzajemną widoczność</li>
            <li>• Leady z rekomendacji są śledzone - zobaczysz ile osób przyszło z polecenia</li>
          </ul>
        </div>

        {/* Add Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">Nowa rekomendacja</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Nazwa firmy *</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm({...form, business_name: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="np. Gabinet Weterynaryjny Łapa"
                  data-testid="rec-business-name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Typ działalności</label>
                <select
                  value={form.business_type}
                  onChange={(e) => setForm({...form, business_type: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  data-testid="rec-business-type"
                >
                  {Object.entries(BUSINESS_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Miasto *</label>
                <input
                  type="text"
                  value={form.business_city}
                  onChange={(e) => setForm({...form, business_city: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="np. Warszawa"
                  data-testid="rec-city"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Województwo</label>
                <select
                  value={form.business_voivodeship}
                  onChange={(e) => setForm({...form, business_voivodeship: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  data-testid="rec-voivodeship"
                >
                  <option value="">Wybierz...</option>
                  {VOIVODESHIPS.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Telefon</label>
                <input
                  type="text"
                  value={form.business_phone}
                  onChange={(e) => setForm({...form, business_phone: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="+48 123 456 789"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  value={form.business_email}
                  onChange={(e) => setForm({...form, business_email: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="kontakt@firma.pl"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Strona www</label>
                <input
                  type="url"
                  value={form.business_website}
                  onChange={(e) => setForm({...form, business_website: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Facebook</label>
                <input
                  type="text"
                  value={form.business_facebook}
                  onChange={(e) => setForm({...form, business_facebook: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                  placeholder="facebook.com/..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Krótki opis firmy</label>
              <input
                type="text"
                value={form.business_description}
                onChange={(e) => setForm({...form, business_description: e.target.value})}
                className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                placeholder="np. Specjalizuje się w ortopedii psów"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Dlaczego polecasz tę firmę? *</label>
              <textarea
                value={form.recommendation_text}
                onChange={(e) => setForm({...form, recommendation_text: e.target.value})}
                className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                rows={3}
                placeholder="Opisz swoje doświadczenia i dlaczego polecasz..."
                data-testid="rec-text"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition disabled:opacity-50"
                data-testid="submit-recommendation-btn"
              >
                {submitting ? 'Zapisywanie...' : 'Dodaj rekomendację'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition"
              >
                Anuluj
              </button>
            </div>
          </form>
        )}

        {/* Recommendations List */}
        {loading ? (
          <div className="text-center py-12 text-zinc-400">Ładowanie...</div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-12 bg-zinc-800/30 rounded-xl border border-zinc-700/50">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">Nie masz jeszcze żadnych rekomendacji</p>
            <p className="text-zinc-500 text-sm">Dodaj pierwszą rekomendację, aby budować swoją sieć partnerów</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {recommendations.map((rec) => {
              const StatusInfo = STATUS_BADGES[rec.status] || STATUS_BADGES.pending;
              const StatusIcon = StatusInfo.icon;
              
              return (
                <div
                  key={rec.id}
                  className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5 hover:border-zinc-600 transition"
                  data-testid={`recommendation-${rec.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{rec.business_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${StatusInfo.className}`}>
                          <StatusIcon className="w-3 h-3" />
                          {StatusInfo.label}
                        </span>
                        <span className="px-2 py-0.5 bg-zinc-700 text-gray-300 rounded-full text-xs">
                          {BUSINESS_TYPES[rec.business_type] || rec.business_type}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-3">
                        {rec.business_city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {rec.business_city}{rec.business_voivodeship ? `, ${rec.business_voivodeship}` : ''}
                          </span>
                        )}
                        {rec.business_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {rec.business_phone}
                          </span>
                        )}
                        {rec.business_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {rec.business_email}
                          </span>
                        )}
                        {rec.business_website && (
                          <a 
                            href={rec.business_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-orange-400 hover:text-orange-300"
                          >
                            <Globe className="w-4 h-4" />
                            Strona www
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>

                      {rec.business_description && (
                        <p className="text-zinc-400 text-sm mb-2">{rec.business_description}</p>
                      )}

                      <div className="bg-zinc-900/50 rounded-lg p-3 mt-3">
                        <p className="text-gray-300 text-sm italic">&ldquo;{rec.recommendation_text}&rdquo;</p>
                      </div>

                      {/* Stats */}
                      {rec.status === 'published' && (
                        <div className="flex gap-4 mt-3 text-xs text-zinc-500">
                          <span>👆 {rec.clicks || 0} kliknięć</span>
                          <span>📩 {rec.leads_generated || 0} leadów</span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="p-2 text-zinc-500 hover:text-red-400 transition"
                      title="Usuń"
                      data-testid={`delete-rec-${rec.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
